import type { User } from "@/common/types/user";
import type { AbilityClass, MongoQuery, SubjectRawRule } from "@casl/ability";
import { Ability, AbilityBuilder } from "@casl/ability";
import type {
  KnownPermissionName,
  KnownRoleName,
} from "@/common/types/permission";
import { userHasPermission } from "@/common/helpers";

type Action = "display" | "read" | "edit" | "impersonate" | "delete";
type Subject = User | "User";

// to allow for type checking on the names of deeply nested fields, they need to be flattened
// see https://casl.js.org/v5/en/advanced/typescript#nested-fields-with-dot-notation
export type FlatUser = User & {
  "personalData.username": User["personalData"]["username"];
  "personalData.gender": User["personalData"]["gender"];
  "personalData.title": User["personalData"]["title"];
  "personalData.firstname": User["personalData"]["firstname"];
  "personalData.lastname": User["personalData"]["lastname"];
  "personalData.company": User["personalData"]["company"];
  "personalData.jobTitle": User["personalData"]["jobTitle"];
  "personalData.email": User["personalData"]["email"];
  "personalData.unconfirmedEmail": User["personalData"]["unconfirmedEmail"];
  "personalData.phone": User["personalData"]["phone"];
  "personalData.mobile": User["personalData"]["mobile"];
  "personalData.street": User["personalData"]["street"];
  "personalData.zipcode": User["personalData"]["zipcode"];
  "personalData.city": User["personalData"]["city"];
  "personalData.country": User["personalData"]["country"];
  "personalData.status": User["personalData"]["status"];
  "settings.userLanguage": User["settings"]["userLanguage"];
  "settings.otpEmail": User["settings"]["otpEmail"];

  // Picture and communication are not actually fields of the user.
  // Still the user instance is needed to decide, whether the given permissions are set for the user that is currently
  // being edited.
  // Defining them on the FlatUser type allows for them to be used as if they were fields of the user.
  picture: never;
  communication: never;
};

export type EtcAbility = Ability<[Action, Subject]>;
export const EtcAbility = Ability as AbilityClass<EtcAbility>;

export const ability = new EtcAbility();

export const useEtcAbility = (): EtcAbility => ability;

export const defineRulesFor = (
  user: User,
): SubjectRawRule<
  Action,
  "User",
  MongoQuery<Record<string | number | symbol, unknown>>
>[] => {
  const { can, rules } = new AbilityBuilder(EtcAbility);

  const p = (permissionName: KnownPermissionName): boolean =>
    userHasPermission(user, permissionName);

  // user management permissions on the own user
  defineUserManagementReadPermissionsWithCondition({ id: user.id }, p, can);
  defineUserManagementEditPermissionsWithCondition({ id: user.id }, p, can);

  // user management permissions on other users
  if (p("OTHER_USER_READ")) {
    defineUserManagementReadPermissionsOnRole("USER", p, can);
  }
  if (p("OTHER_USER_EDIT")) {
    defineUserManagementEditPermissionsOnRole("USER", p, can);
  }
  if (p("OTHER_ACCOUNT_MANAGER_READ")) {
    defineUserManagementReadPermissionsOnRole("ACCOUNT_MANAGER", p, can);
  }
  if (p("OTHER_ACCOUNT_MANAGER_EDIT")) {
    defineUserManagementEditPermissionsOnRole("ACCOUNT_MANAGER", p, can);
  }
  if (p("OTHER_ADMIN_READ")) {
    defineUserManagementReadPermissionsOnRole("ADMIN", p, can);
  }
  if (p("OTHER_ADMIN_EDIT")) {
    defineUserManagementEditPermissionsOnRole("ADMIN", p, can);
  }

  // impersonate permissions
  if (p("IMPERSONATE_USER")) {
    can("impersonate", "User", { roles: { $elemMatch: { roleName: "USER" } } });
  }
  if (p("IMPERSONATE_ACCOUNT_MANAGER")) {
    can("impersonate", "User", {
      roles: { $elemMatch: { roleName: "ACCOUNT_MANAGER" } },
    });
  }
  if (p("IMPERSONATE_ADMIN")) {
    can("impersonate", "User", {
      roles: { $elemMatch: { roleName: "ADMIN" } },
    });
  }

  // impersonate permissions
  if (p("DELETE_USER")) {
    can("delete", "User", { roles: { $elemMatch: { roleName: "USER" } } });
  }
  if (p("DELETE_ACCOUNT_MANAGER")) {
    can("delete", "User", {
      roles: { $elemMatch: { roleName: "ACCOUNT_MANAGER" } },
    });
  }
  if (p("DELETE_ADMIN")) {
    can("delete", "User", {
      roles: { $elemMatch: { roleName: "ADMIN" } },
    });
  }

  return rules;
};

/**
 * Defines the read permissions a user has on another user, limited by a certain condition
 *
 * @param condition the condition limiting, on which subjects the read permissions are granted
 * @param p the function used to determine whether the user has a certain permission
 * @param can the 'can' function of he AbilityBuilder used to define abilities
 */
const defineUserManagementReadPermissionsWithCondition = (
  condition: MongoQuery<FlatUser>,
  p: (permissionName: KnownPermissionName) => boolean,
  can: AbilityBuilder<EtcAbility>["can"],
) => {
  // Defines a read permission on a certain field of a user, if the permission needed for that field is set
  const defineReadPermissionOnField = (
    permissionNeeded: KnownPermissionName,
    field: keyof FlatUser,
  ) => {
    if (p(permissionNeeded)) {
      can<FlatUser>("read", "User", field, condition);
    }
  };

  defineReadPermissionOnField("USERMANAGEMENT_COMMUNICATION", "communication");
  defineReadPermissionOnField("USERMANAGEMENT_DASHBOARD", "dashboard");
  defineReadPermissionOnField("USERMANAGEMENT_PERMISSIONS", "permissions");
  defineReadPermissionOnField(
    "USERMANAGEMENT_REGISTRATIONTEXT",
    "registrationText",
  );
  defineReadPermissionOnField("USERMANAGEMENT_PICTURE", "picture");
  defineReadPermissionOnField("USERMANAGEMENT_ROLES", "roles");
  defineReadPermissionOnField("USERMANAGEMENT_SETTINGS", "settings");
  defineReadPermissionOnField("USERMANAGEMENT_STATISTICS", "statistics");
  defineReadPermissionOnField("USERMANAGEMENT_TOUR", "tour");
  defineReadPermissionOnField("USERMANAGEMENT_VISIBILITY", "visibility");

  // Permissions on settings data fields
  defineReadPermissionOnField(
    "USERMANAGEMENT_SETTINGS_FIELD_MFA",
    "settings.otpEmail",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_SETTINGS_FIELD_LANGUAGE",
    "settings.userLanguage",
  );

  // Permissions on the personal data fields
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_CITY",
    "personalData.city",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_COMPANY",
    "personalData.company",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_COUNTRY",
    "personalData.country",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_EMAIL",
    "personalData.email",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_UNCONFIRMEDEMAIL",
    "personalData.unconfirmedEmail",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_FIRSTNAME",
    "personalData.firstname",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_GENDER",
    "personalData.gender",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_JOBTITLE",
    "personalData.jobTitle",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_LASTNAME",
    "personalData.lastname",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_MOBILE",
    "personalData.mobile",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_PHONE",
    "personalData.phone",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_STATUS",
    "personalData.status",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_STREET",
    "personalData.street",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_TITLE",
    "personalData.title",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_USERNAME",
    "personalData.username",
  );
  defineReadPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_ZIPCODE",
    "personalData.zipcode",
  );
};

/**
 * Defines the edit permissions a user has on another user, limited by a certain condition
 *
 * @param condition the condition limiting, on which subjects the read permissions are granted
 * @param p the function used to determine whether the user has a certain permission
 * @param can the 'can' function of the AbilityBuilder used to define abilities
 */
const defineUserManagementEditPermissionsWithCondition = (
  condition: MongoQuery<FlatUser>,
  p: (permissionName: KnownPermissionName) => boolean,
  can: AbilityBuilder<EtcAbility>["can"],
) => {
  // Defines a read permission on a certain field of a user, if the permission needed for that field is set
  const defineEditPermissionOnField = (
    permissionNeeded: KnownPermissionName,
    field: keyof FlatUser,
  ) => {
    if (p(permissionNeeded)) {
      can<FlatUser>("edit", "User", field, condition);
    }
  };

  defineEditPermissionOnField("USERMANAGEMENT_DASHBOARD_EDIT", "dashboard");
  defineEditPermissionOnField("USERMANAGEMENT_PERMISSIONS_EDIT", "permissions");
  defineEditPermissionOnField(
    "USERMANAGEMENT_REGISTRATIONTEXT_EDIT",
    "registrationText",
  );
  defineEditPermissionOnField("USERMANAGEMENT_PICTURE_EDIT", "picture");
  defineEditPermissionOnField("USERMANAGEMENT_ROLES_EDIT", "roles");
  defineEditPermissionOnField("USERMANAGEMENT_SETTINGS_EDIT", "settings");
  defineEditPermissionOnField("USERMANAGEMENT_TOUR_EDIT", "tour");
  defineEditPermissionOnField("USERMANAGEMENT_VISIBILITY_EDIT", "visibility");

  // Permissions on the personal data fields
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_CITY_EDIT",
    "personalData.city",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_COMPANY_EDIT",
    "personalData.company",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_COUNTRY_EDIT",
    "personalData.country",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_UNCONFIRMEDEMAIL_EDIT",
    "personalData.unconfirmedEmail",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_FIRSTNAME_EDIT",
    "personalData.firstname",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_GENDER_EDIT",
    "personalData.gender",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_JOBTITLE_EDIT",
    "personalData.jobTitle",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_LASTNAME_EDIT",
    "personalData.lastname",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_MOBILE_EDIT",
    "personalData.mobile",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_PHONE_EDIT",
    "personalData.phone",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_STATUS_EDIT",
    "personalData.status",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_STREET_EDIT",
    "personalData.street",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_TITLE_EDIT",
    "personalData.title",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_USERNAME_EDIT",
    "personalData.username",
  );
  defineEditPermissionOnField(
    "USERMANAGEMENT_PERSONALDATA_FIELD_ZIPCODE_EDIT",
    "personalData.zipcode",
  );
};

/**
 * Defines the read permissions a user has on another user (subject) with a certain role
 *
 * @param subjectRoleName the name of the subject's role
 * @param p the function used to determine whether the user has a certain permission
 * @param can the 'can' function of the AbilityBuilder used to define abilities
 */
const defineUserManagementReadPermissionsOnRole = (
  subjectRoleName: KnownRoleName,
  p: (permissionName: KnownPermissionName) => boolean,
  can: AbilityBuilder<EtcAbility>["can"],
) => {
  const roleCondition = {
    roles: { $elemMatch: { roleName: subjectRoleName } },
  };
  defineUserManagementReadPermissionsWithCondition(roleCondition, p, can);
};

/**
 * Defines the edit permissions a user has on another user (subject) with a certain role
 *
 * @param subjectRoleName the name of the subject's role
 * @param p the function used to determine whether the user has a certain permission
 * @param can the 'can' function of the AbilityBuilder used to define abilities
 */
const defineUserManagementEditPermissionsOnRole = (
  subjectRoleName: KnownRoleName,
  p: (permissionName: KnownPermissionName) => boolean,
  can: AbilityBuilder<EtcAbility>["can"],
) => {
  const roleCondition = {
    roles: { $elemMatch: { roleName: subjectRoleName } },
  };
  defineUserManagementEditPermissionsWithCondition(roleCondition, p, can);
};
